import { useMemo, useState } from 'react';
import { CHORD_TYPES, type ChordTypeId } from '@/music/chords';
import {
  SCALE_HARMONIES,
  SCALE_HARMONY_IDS,
  harmoniseScale,
  placeVoicingsInNeckWindow,
  type ScaleHarmonyId,
} from '@/music/scaleHarmony';
import {
  generateChordVoicing,
  sourceRotationForFinishedInversion,
  voicingIdentityLabel,
  type Inversion,
} from '@/music/voicing';
import {
  DROP_VOICING_FAMILY_IDS,
  primaryVoicingFamily,
  voicingFamilyLabel,
  type VoicingFamily,
} from '@/music/voicingFamilies';
import { useStore } from '@/state/store';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { ReferencePage } from '@/components/ReferencePage';
import type { InspectChord } from '@/components/inspectorContext';

const FINISHED_INVERSIONS: { id: Inversion; label: string }[] = [
  { id: 0, label: 'Root position' },
  { id: 1, label: '1st inversion' },
  { id: 2, label: '2nd inversion' },
  { id: 3, label: '3rd inversion' },
];

const SCALE_TONICS = [
  'C',
  'C#',
  'Db',
  'D',
  'D#',
  'Eb',
  'E',
  'F',
  'F#',
  'Gb',
  'G',
  'G#',
  'Ab',
  'A',
  'A#',
  'Bb',
  'B',
] as const;

function displayAccidentals(text: string): string {
  return text.replaceAll('#', '♯').replaceAll('b', '♭');
}

const CHORD_MEMBERS = ['Root', 'Third', 'Fifth', 'Seventh'] as const;

function topVoiceMember(rotation: Inversion): (typeof CHORD_MEMBERS)[number] {
  return CHORD_MEMBERS[(rotation + CHORD_MEMBERS.length - 1) % CHORD_MEMBERS.length];
}

export function ScaleHarmonyPage() {
  const stringSet = useStore((state) => state.stringSet);
  const setStringSet = useStore((state) => state.setStringSet);
  const defaultFamily = useStore((state) => state.voicingFamily);
  const avoidB9 = useStore((state) => state.avoidB9);
  const [tonic, setTonic] = useState('C');
  const [scaleId, setScaleId] = useState<ScaleHarmonyId>('major');
  const [voicingFamily, setVoicingFamily] = useState<VoicingFamily>(
    defaultFamily === 'joe-pass' ? 'drop-2' : primaryVoicingFamily(defaultFamily),
  );
  const [finishedInversion, setFinishedInversion] = useState<Inversion>(0);

  const harmony = useMemo(() => harmoniseScale(tonic, scaleId), [tonic, scaleId]);
  const sourceRotation = sourceRotationForFinishedInversion(voicingFamily, finishedInversion);
  const voicings = useMemo(
    () =>
      placeVoicingsInNeckWindow(
        harmony.map((chord) =>
          generateChordVoicing(
            displayAccidentals(chord.root),
            chord.chordType,
            sourceRotation,
            stringSet,
            voicingFamily,
          ),
        ),
      ),
    [harmony, sourceRotation, stringSet, voicingFamily],
  );

  return (
    <ReferencePage
      className="scale-harmony-page"
      title="Scale harmony"
      controls={
        <>
          <label className="control-group">
            <span className="label">Tonic</span>
            <select
              value={tonic}
              onChange={(event) => setTonic(event.target.value)}
              aria-label="Scale tonic"
            >
              {SCALE_TONICS.map((note) => (
                <option key={note} value={note}>
                  {displayAccidentals(note)}
                </option>
              ))}
            </select>
          </label>

          <label className="control-group">
            <span className="label">Scale</span>
            <select
              value={scaleId}
              onChange={(event) => setScaleId(event.target.value as ScaleHarmonyId)}
              aria-label="Scale"
            >
              {SCALE_HARMONY_IDS.map((id) => (
                <option key={id} value={id}>
                  {SCALE_HARMONIES[id].label}
                </option>
              ))}
            </select>
          </label>

          <div className="control-group">
            <span className="label">Family</span>
            <div className="segmented" role="group" aria-label="Voicing family">
              {DROP_VOICING_FAMILY_IDS.map((family) => (
                <button
                  key={family}
                  type="button"
                  className={voicingFamily === family ? 'active' : ''}
                  aria-pressed={voicingFamily === family}
                  onClick={() => setVoicingFamily(family)}
                  data-testid={`scale-family-${family}`}
                >
                  {voicingFamilyLabel(family)}
                </button>
              ))}
            </div>
          </div>

          <div className="control-group">
            <span className="label">Register</span>
            <div className="segmented" role="group" aria-label="Register">
              {(['middle', 'upper'] as const).map((register) => (
                <button
                  key={register}
                  type="button"
                  className={stringSet === register ? 'active' : ''}
                  aria-pressed={stringSet === register}
                  onClick={() => setStringSet(register)}
                  data-testid={`scale-register-${register}`}
                >
                  {register === 'middle' ? 'Lower' : 'Upper'}
                </button>
              ))}
            </div>
          </div>

          <label className="control-group scale-harmony-pattern-control">
            <span className="label">Inversion pattern</span>
            <select
              value={finishedInversion}
              onChange={(event) => setFinishedInversion(Number(event.target.value) as Inversion)}
              aria-label="Inversion pattern"
            >
              {FINISHED_INVERSIONS.map((inversion) => {
                const rotation = sourceRotationForFinishedInversion(voicingFamily, inversion.id);
                return (
                  <option key={inversion.id} value={inversion.id}>
                    {inversion.label} · {topVoiceMember(rotation)} on top
                  </option>
                );
              })}
            </select>
          </label>
        </>
      }
    >
      <ol className="scale-harmony-grid" aria-label="Harmonised scale voicings">
        {harmony.map((chord, index) => {
          const displayRoot = displayAccidentals(chord.root);
          const voicing = voicings[index];
          return (
            <li key={chord.degree} className="scale-harmony-degree" data-testid="scale-degree">
              <header>
                <span>{chord.numeral}</span>
                <h3>{displayAccidentals(chord.symbol)}</h3>
                <small>{CHORD_TYPES[chord.chordType].name}</small>
              </header>
              {voicing ? (
                <PlayableDiagram
                  chord={toInspect(voicing, displayRoot, chord.chordType, stringSet)}
                  caption={voicingIdentityLabel(voicing, displayRoot, chord.chordType)}
                  showTitle={false}
                  highlightAvoid={avoidB9}
                />
              ) : (
                <div className="chord-card" aria-disabled="true">
                  <div className="card-caption">No voicing</div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </ReferencePage>
  );
}

function toInspect(
  voicing: NonNullable<ReturnType<typeof generateChordVoicing>>,
  root: string,
  chordType: ChordTypeId,
  stringSet: 'middle' | 'upper',
): InspectChord {
  return {
    fingering: voicing,
    rootDisplay: root,
    chordType,
    symbol: voicing.symbol,
    inversion: voicing.inversion,
    stringSet,
  };
}
